
import { useState } from "react";
import { Building2, Calendar, BedDouble, CheckCircle, Eye, EyeOff, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { formatDate, formatMonthYear, formatWeekdayShort } from "@/lib/dateFormat";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, Note, ReportBody, ReportHeader, TableScroll,
  PresetSelect, monthRange, pct, th, td, useRange, useReport, type ExportSpec,
} from "../ReportKit";

type Tab = "timeline" | "venues" | "rooms";

interface VenueBooking {
  event_id: number; event_name: string; venue_id: number; venue_name: string | null;
  client: string | null; start_date: string; end_date: string; status: string;
}
interface RoomBookingRow {
  booking_id: number; room_id: number; room_name: string | null; room_type: string | null;
  event_id: number | null; event_name: string | null; client: string | null; guest_name: string | null;
  check_in: string; check_out: string; nights: number; status: string;
}
interface Utilization {
  period: { from: string; to: string };
  timeline: {
    date: string; day_label: string; is_today: boolean; is_sunday: boolean;
    venues: VenueBooking[]; rooms: RoomBookingRow[]; occupied: boolean;
  }[];
  venues: {
    id: number; name: string; capacity: number | null; location: string | null; is_active: boolean;
    days_booked: number; bookings: VenueBooking[];
  }[];
  rooms: { id: number; name: string; type: string | null; capacity: number | null; bookings: RoomBookingRow[] }[];
  summary: {
    total_days: number; busy_days: number; free_days: number;
    venue_days_booked: number; venue_days_available: number; occupancy_rate: number;
    venue_booking_count: number; venues_with_bookings: number; drafts_excluded: number;
    room_booking_count: number; room_nights_booked: number; room_nights_available: number;
    room_occupancy_rate: number; rooms_with_bookings: number;
  };
}

const STATUS_COLOR: Record<string, string> = {
  confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  booked: "bg-indigo-100 text-indigo-700 border-indigo-200",
  checked_in: "bg-teal-100 text-teal-700 border-teal-200",
  checked_out: "bg-slate-100 text-slate-600 border-slate-200",
};
const EVENT_COLORS = ["bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-amber-500", "bg-rose-500", "bg-cyan-500", "bg-indigo-500", "bg-orange-500"];

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${STATUS_COLOR[status] ?? "bg-stone-100 text-stone-600 border-stone-200"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

const dateSpan = (a: string, b: string) => (a === b ? formatDate(a) : `${formatDate(a)} – ${formatDate(b)}`);

const statusText = (s: string | null | undefined) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "");

function utilizationNote(s: Utilization["summary"]): string {
  return "A hall counts as taken on every day from an event’s start date to its end date. Only confirmed, in-progress and completed events count"
    + (s.drafts_excluded > 0 ? ` — ${s.drafts_excluded} draft event${s.drafts_excluded === 1 ? " is" : "s are"} not shown` : "")
    + "; cancelled events and room bookings are left out. Occupancy = hall-days booked ÷ (active halls × days).";
}

/** Excel / Print: the KPIs, the day-by-day list (as filtered on screen), each venue and every hall and room booking. */
function buildExport(d: Utilization, showFree: boolean): ExportSpec {
  const s = d.summary;
  const days = showFree ? d.timeline : d.timeline.filter((day) => day.occupied);
  return {
    title: "Venue Utilization",
    fileName: "venue-utilization",
    period: { from: d.period.from, to: d.period.to },
    summary: [
      { label: "Hall occupancy", value: s.occupancy_rate, type: "percent" },
      { label: "Hall-days booked", value: s.venue_days_booked, type: "number" },
      { label: "Hall-days available", value: s.venue_days_available, type: "number" },
      { label: "Busy days", value: s.busy_days, type: "number" },
      { label: "Days with no hall booked", value: s.free_days, type: "number" },
      { label: "Days in period", value: s.total_days, type: "number" },
      { label: "Hall bookings", value: s.venue_booking_count, type: "number" },
      { label: "Venues used", value: s.venues_with_bookings, type: "number" },
      { label: "Room bookings", value: s.room_booking_count, type: "number" },
      { label: "Room nights", value: s.room_nights_booked, type: "number" },
      ...(s.room_nights_available
        ? [
          { label: "Room nights available", value: s.room_nights_available, type: "number" as const },
          { label: "Room occupancy", value: s.room_occupancy_rate, type: "percent" as const },
        ]
        : []),
    ],
    tables: [
      {
        title: "Day by day",
        columns: [{ label: "Date", type: "date" }, { label: "Day" }, { label: "Halls" }, { label: "Rooms" }],
        rows: days.map((day) => [
          day.date,
          formatWeekdayShort(day.date),
          day.venues.map((v) => `${v.venue_name ?? "Hall"}: ${v.event_name}${v.client ? ` (${v.client})` : ""}`).join("; ") || (day.occupied ? "" : "Free"),
          day.rooms.map((r) => `${r.room_name ?? "Room"}${r.event_name || r.guest_name ? `: ${r.event_name ?? r.guest_name}` : ""}`).join("; "),
        ]),
      },
      {
        title: "By venue",
        columns: [
          { label: "Venue" }, { label: "Location" }, { label: "Capacity", type: "number" },
          { label: "Days booked", type: "number" }, { label: "Bookings", type: "number" }, { label: "Status" },
        ],
        rows: d.venues.map((v) => [v.name, v.location, v.capacity, v.days_booked, v.bookings.length, v.is_active ? "Active" : "Archived"]),
      },
      {
        title: "Hall bookings",
        columns: [
          { label: "Venue" }, { label: "From", type: "date" }, { label: "To", type: "date" },
          { label: "Event" }, { label: "Client" }, { label: "Status" },
        ],
        rows: d.venues.flatMap((v) => v.bookings.map((b) => [v.name, b.start_date, b.end_date, b.event_name, b.client, statusText(b.status)])),
      },
      {
        title: "Room bookings",
        columns: [
          { label: "Room" }, { label: "Room type" }, { label: "Check-in", type: "date" }, { label: "Check-out", type: "date" },
          { label: "Nights", type: "number" }, { label: "Event / guest" }, { label: "Status" },
        ],
        rows: d.rooms.flatMap((r) => r.bookings.map((b) => [
          r.name, r.type, b.check_in, b.check_out, b.nights, b.event_name ?? b.guest_name, statusText(b.status),
        ])),
      },
    ],
    notes: [utilizationNote(s)],
  };
}

export default function VenueUtilizationPage() {
  const [monthOffset, setMonthOffset] = useState(0);
  const range = useRange(monthRange(0));
  const { from, to } = range.applied;
  const [tab, setTab] = useState<Tab>("timeline");
  const [showFree, setShowFree] = useState(false);

  const goMonth = (offset: number) => {
    setMonthOffset(offset);
    range.set(monthRange(offset));
  };
  // Label the month when the range is exactly one calendar month
  const asMonth = monthRange(monthOffset);
  const isWholeMonth = asMonth.from === from && asMonth.to === to;

  const state = useReport<Utilization>(
    async () => (await reportsApi.venueUtilization({ from_date: from, to_date: to })).data,
    [from, to],
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader icon={Building2} iconClass="text-teal-600" title="Venue Utilization" subtitle="Which halls and rooms are taken, day by day">
          <HeaderTools>
            <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-xl p-1">
              <button type="button" onClick={() => goMonth(monthOffset - 1)} aria-label="Previous month"
                className="w-10 h-8 rounded-lg flex items-center justify-center text-stone-600 hover:bg-stone-100">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => goMonth(0)}
                className="flex-1 sm:flex-none min-w-[120px] h-8 px-2 rounded-lg text-sm font-semibold text-stone-700 hover:bg-stone-100">
                {isWholeMonth ? formatMonthYear(from) : "Custom range"}
              </button>
              <button type="button" onClick={() => goMonth(monthOffset + 1)} aria-label="Next month"
                className="w-10 h-8 rounded-lg flex items-center justify-center text-stone-600 hover:bg-stone-100">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <PresetSelect from={range.from} to={range.to} onPick={range.set} />
            <DateRangePicker fromDate={range.from} toDate={range.to} onFromChange={range.setFrom} onToChange={range.setTo} />
            <ExportButtons
              build={() => (state.data ? buildExport(state.data, showFree) : null)}
              disabled={state.loading || !state.data}
            />
          </HeaderTools>
        </ReportHeader>

        <ReportBody state={state}>
          {(d) => <UtilizationView d={d} tab={tab} setTab={setTab} showFree={showFree} setShowFree={setShowFree} />}
        </ReportBody>
      </div>
    </DashboardLayout>
  );
}

function UtilizationView({ d, tab, setTab, showFree, setShowFree }: {
  d: Utilization; tab: Tab; setTab: (t: Tab) => void; showFree: boolean; setShowFree: (fn: (v: boolean) => boolean) => void;
}) {
  const s = d.summary;
  const colorMap: Record<number, string> = {};
  let i = 0;
  [...d.venues.flatMap((v) => v.bookings.map((b) => b.event_id)), ...d.rooms.flatMap((r) => r.bookings.map((b) => b.event_id ?? 0))]
    .forEach((id) => { if (id && !(id in colorMap)) colorMap[id] = EVENT_COLORS[i++ % EVENT_COLORS.length]; });

  const visibleDays = showFree ? d.timeline : d.timeline.filter((day) => day.occupied);
  const tabs: { key: Tab; label: string; icon: typeof Calendar }[] = [
    { key: "timeline", label: "Day by day", icon: Calendar },
    { key: "venues", label: "By venue", icon: Building2 },
    { key: "rooms", label: "By room", icon: BedDouble },
  ];

  return (
    <>
      <KpiGrid>
        <Kpi label="Hall occupancy" icon={CheckCircle} tone="teal" value={pct(s.occupancy_rate)} sub={`${s.venue_days_booked} of ${s.venue_days_available} hall-days booked`} />
        <Kpi label="Busy days" icon={Calendar} tone="emerald" value={`${s.busy_days} / ${s.total_days}`} sub={`${s.free_days} day${s.free_days === 1 ? "" : "s"} with no hall booked`} />
        <Kpi label="Hall bookings" icon={Building2} tone="blue" value={s.venue_booking_count} sub={`${s.venues_with_bookings} venue${s.venues_with_bookings === 1 ? "" : "s"} used`} />
        <Kpi label="Room nights" icon={BedDouble} tone="violet" value={s.room_nights_booked}
          sub={s.room_nights_available ? `${pct(s.room_occupancy_rate)} of ${s.room_nights_available} available` : "No rooms set up"} />
      </KpiGrid>

      <section className="bg-white rounded-xl border border-stone-200 min-w-0">
        <div className="flex items-center border-b border-stone-100 px-2 sm:px-4 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`shrink-0 flex items-center gap-1.5 px-3 h-11 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? "border-teal-500 text-teal-700" : "border-transparent text-stone-500 hover:text-stone-700"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "timeline" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-stone-500">{d.timeline.filter((x) => x.occupied).length} day(s) with a booking</p>
                <button type="button" onClick={() => setShowFree((v) => !v)}
                  className="h-9 inline-flex items-center gap-1.5 px-2 rounded-lg text-xs text-stone-600 hover:bg-stone-100">
                  {showFree ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {showFree ? "Hide free days" : "Show all days"}
                </button>
              </div>
              {visibleDays.length === 0 ? (
                <p className="text-sm text-stone-400 py-8 text-center">No bookings in this period</p>
              ) : (
                <div className="space-y-2">
                  {visibleDays.map((day) => (
                    <div key={day.date} className={`rounded-lg border ${day.is_today ? "border-teal-300 bg-teal-50/50" : day.occupied ? "border-stone-200" : "border-stone-100 bg-stone-50/50"}`}>
                      <div className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${day.is_today ? "bg-teal-500 text-white" : day.is_sunday ? "bg-red-100 text-red-600" : "bg-stone-200 text-stone-600"}`}>
                            {Number(day.date.slice(8, 10))}
                          </span>
                          <span className="text-xs font-semibold text-stone-700 truncate">{day.day_label}</span>
                          {day.is_today && <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">Today</span>}
                        </div>
                        {!day.occupied && <span className="text-[10px] text-stone-400">Free</span>}
                      </div>
                      {day.venues.length > 0 && (
                        <div className="px-3 py-2 space-y-1.5 border-t border-stone-100">
                          {day.venues.map((v) => (
                            <div key={v.event_id} className="flex items-center gap-2 flex-wrap text-xs">
                              <span className={`w-2 h-2 rounded-full shrink-0 ${colorMap[v.event_id] ?? "bg-stone-400"}`} />
                              <span className="font-medium text-stone-800">{v.venue_name}</span>
                              <span className="text-stone-400">→</span>
                              <span className="text-stone-700">{v.event_name}</span>
                              {v.client && <span className="text-stone-400">({v.client})</span>}
                              {v.start_date !== v.end_date && <span className="text-stone-400">{dateSpan(v.start_date, v.end_date)}</span>}
                              <StatusBadge status={v.status} />
                            </div>
                          ))}
                        </div>
                      )}
                      {day.rooms.length > 0 && (
                        <div className="px-3 py-2 border-t border-stone-100 flex flex-wrap gap-2">
                          {day.rooms.map((r) => (
                            <div key={r.booking_id} className="flex items-center gap-1.5 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1 text-xs">
                              <BedDouble className="w-3 h-3 text-violet-500" />
                              <span className="font-medium text-stone-700">{r.room_name}</span>
                              {(r.event_name || r.guest_name) && <span className="text-stone-500">· {r.event_name ?? r.guest_name}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "venues" && (
            <div className="space-y-4">
              {d.venues.length === 0 && <p className="text-sm text-stone-400 py-8 text-center">No venues set up</p>}
              {d.venues.map((venue) => (
                <div key={venue.id} className="rounded-lg border border-stone-200 overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-2 bg-stone-50 px-4 py-3 border-b border-stone-200">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <Building2 className="w-4 h-4 text-teal-600" />
                      <span className="text-sm font-semibold text-stone-800">{venue.name}</span>
                      {!venue.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-600">archived</span>}
                      {venue.location && <span className="flex items-center gap-0.5 text-xs text-stone-400"><MapPin className="w-3 h-3" />{venue.location}</span>}
                      {venue.capacity ? <span className="text-[10px] bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded-full">Cap: {venue.capacity}</span> : null}
                    </div>
                    <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
                      {venue.days_booked} of {s.total_days} days booked
                    </span>
                  </div>
                  {venue.bookings.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-stone-400">No bookings in this period</div>
                  ) : (
                    <div className="p-4 pt-0">
                      <TableScroll minWidth={520}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-100">
                              <th className={`${th} text-left`}>Dates</th>
                              <th className={`${th} text-left`}>Event</th>
                              <th className={`${th} text-left`}>Client</th>
                              <th className={`${th} text-left`}>Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-50">
                            {venue.bookings.map((b) => (
                              <tr key={b.event_id}>
                                <td className={`${td} text-xs`}>
                                  <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${colorMap[b.event_id] ?? "bg-stone-400"}`} />
                                    <span className="font-medium text-stone-700">{dateSpan(b.start_date, b.end_date)}</span>
                                    <span className="text-[10px] text-stone-400">{formatWeekdayShort(b.start_date)}</span>
                                  </div>
                                </td>
                                <td className={`${td} text-xs font-medium text-stone-800`}>{b.event_name}</td>
                                <td className={`${td} text-xs text-stone-500`}>{b.client ?? "—"}</td>
                                <td className={td}><StatusBadge status={b.status} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "rooms" && (
            <div className="space-y-4">
              {d.rooms.every((r) => r.bookings.length === 0) ? (
                <p className="text-sm text-stone-400 py-8 text-center">No room bookings in this period</p>
              ) : d.rooms.filter((r) => r.bookings.length > 0).map((room) => (
                <div key={room.id} className="rounded-lg border border-stone-200 overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-2 bg-stone-50 px-4 py-3 border-b border-stone-200">
                    <div className="flex items-center gap-2">
                      <BedDouble className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-semibold text-stone-800">{room.name}</span>
                      {room.type && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">{room.type}</span>}
                    </div>
                    <span className="text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full border border-violet-100">
                      {room.bookings.length} booking{room.bookings.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="p-4 pt-0">
                    <TableScroll minWidth={560}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-stone-100">
                            <th className={`${th} text-left`}>Check-in</th>
                            <th className={`${th} text-left`}>Check-out</th>
                            <th className={`${th} text-right`}>Nights</th>
                            <th className={`${th} text-left`}>Event / guest</th>
                            <th className={`${th} text-left`}>Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-50">
                          {room.bookings.map((b) => (
                            <tr key={b.booking_id}>
                              <td className={`${td} text-xs text-stone-700`}>{formatDate(b.check_in)}</td>
                              <td className={`${td} text-xs text-stone-700`}>{formatDate(b.check_out)}</td>
                              <td className={`${td} text-right text-xs font-semibold text-violet-700`}>{b.nights}</td>
                              <td className={`${td} text-xs text-stone-700`}>{b.event_name ?? b.guest_name ?? "—"}</td>
                              <td className={td}><StatusBadge status={b.status} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableScroll>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Card>
        <Note>{utilizationNote(s)}</Note>
      </Card>
    </>
  );
}
